// Sign-in page. Registration is closed: accounts are created by the owner, a
// new user sets the password through "Passwort vergessen?".
import { ArrowLeft, CircleAlert, CircleCheck, LoaderCircle, MailCheck } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import { Logo } from '../components/Logo';
import { PasswordField } from '../components/PasswordField';
import { RESET_LINK_MINUTES, authErrorMessage, requestPasswordReset, signIn } from '../lib/authClient';

type Mode = 'sign-in' | 'forgot' | 'sent';

export function SignInView({ notice, onSignedIn }: { notice?: string | undefined; onSignedIn: () => void }) {
  const [mode, setMode] = useState<Mode>('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const emailId = useId();
  const errorId = useId();

  const go = (next: Mode) => {
    setError(null);
    setMode(next);
  };

  const submitSignIn = async (event: FormEvent) => {
    event.preventDefault();
    if (!email.trim() || !password) {
      setError('Bitte gib E-Mail-Adresse und Passwort ein.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
      onSignedIn();
    } catch (err) {
      setError(authErrorMessage(err, 'sign-in'));
      setBusy(false);
    }
  };

  const submitForgot = async (event: FormEvent) => {
    event.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError('Bitte gib eine gültige E-Mail-Adresse ein.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await requestPasswordReset(email.trim());
      go('sent');
    } catch (err) {
      setError(authErrorMessage(err, 'request-reset'));
    } finally {
      setBusy(false);
    }
  };

  const errorBox = error && (
    <p id={errorId} className="inline-error" role="alert">
      <CircleAlert size={18} aria-hidden="true" />
      {error}
    </p>
  );

  const emailField = (
    <div className="field">
      <label htmlFor={emailId} className="field__label">
        E-Mail-Adresse
      </label>
      <input
        id={emailId}
        className="field__input"
        type="email"
        autoComplete="username"
        inputMode="email"
        autoCapitalize="none"
        spellCheck={false}
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        required
      />
    </div>
  );

  return (
    <main className="auth">
      <section className="auth__card glass glass--strong">
        <Logo size={64} className="auth__logo" />
        {mode === 'sign-in' && (
          <>
            <h1 className="auth__title">Willkommen zurück</h1>
            <p className="auth__sub">Melde dich an, um deine Habits abzuhaken.</p>
            <form className="form" onSubmit={submitSignIn} noValidate>
              {notice && (
                <p className="inline-success" role="status">
                  <CircleCheck size={18} aria-hidden="true" />
                  {notice}
                </p>
              )}
              {emailField}
              <PasswordField
                label="Passwort"
                autoComplete="current-password"
                value={password}
                onChange={setPassword}
                invalid={!!error}
                describedBy={error ? errorId : undefined}
              />
              {errorBox}
              <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
                {busy && <LoaderCircle size={18} className="spin" aria-hidden="true" />}
                {busy ? 'Anmelden …' : 'Anmelden'}
              </button>
              <button type="button" className="link-btn auth__link" onClick={() => go('forgot')}>
                Passwort vergessen?
              </button>
            </form>
          </>
        )}
        {mode === 'forgot' && (
          <>
            <h1 className="auth__title">Passwort festlegen</h1>
            <p className="auth__sub">Wir schicken dir einen Link, mit dem du ein neues Passwort festlegst – auch für dein erstes.</p>
            <form className="form" onSubmit={submitForgot} noValidate>
              {emailField}
              {errorBox}
              <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
                {busy && <LoaderCircle size={18} className="spin" aria-hidden="true" />}
                Link senden
              </button>
              <button type="button" className="link-btn auth__link" onClick={() => go('sign-in')}>
                <ArrowLeft size={15} aria-hidden="true" /> Zurück zur Anmeldung
              </button>
            </form>
          </>
        )}
        {mode === 'sent' && (
          <>
            <span className="auth__icon" aria-hidden="true">
              <MailCheck size={26} />
            </span>
            <h1 className="auth__title">Schau in dein Postfach</h1>
            <p className="auth__sub">
              Falls ein Konto zu <strong>{email.trim()}</strong> existiert, ist ein Link unterwegs. Er gilt {RESET_LINK_MINUTES} Minuten.
            </p>
            <button type="button" className="btn btn--glass btn--block" onClick={() => go('sign-in')}>
              Zur Anmeldung
            </button>
          </>
        )}
      </section>
      <p className="auth__foot">Neue Konten werden vom Betreiber angelegt.</p>
    </main>
  );
}
